import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Modal } from 'atoms';
import { MoneyCell, PopupContainer } from 'molecules';
import { usePaymentMutations } from 'core/hooks';

import * as L from '../StudentDetail.styled';
import { formatMoneyCurrency } from './format';
import { IAgreement, IContribution, IPayment } from './types';

interface IPickContributionForPaymentPopupProps {
  payment: IPayment;
  agreement: IAgreement;
  studentId: string;
  onClose: () => void;
  onLinked?: () => void;
}

/**
 * Migration helper: when an orphan `Payment` (`payment.contribution ==
 * null`) is found in the data, this popup lets the operator attach it to
 * one of the agreement's unpaid scheduled installments.
 *
 * Only unpaid contributions appear in the list (matching the backend
 * rule: linking to an already-closed Contribution is rejected with
 * `CONTRIBUTION_ALREADY_LINKED`). Sum/currency mismatches are surfaced
 * as soft warnings — the backend still validates and may reject.
 *
 * The popup is purely transitional: with the new schedule-first invariant
 * no further orphan Payments are created, so once the legacy backlog is
 * worked through the entry point disappears entirely.
 */
export const PickContributionForPaymentPopup = memo(
  ({
    payment,
    agreement,
    studentId,
    onClose,
    onLinked,
  }: IPickContributionForPaymentPopupProps) => {
    const { t } = useTranslation();
    const { linkContribution, loading } = usePaymentMutations(studentId);

    const candidates: IContribution[] = useMemo(
      () =>
        (agreement.contributions?.edges || [])
          .map((edge) => edge?.node)
          .filter((node): node is IContribution => Boolean(node))
          .filter((node) => !node.complete),
      [agreement.contributions],
    );

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const selected = useMemo(
      () => candidates.find((c) => c.id === selectedId) || null,
      [candidates, selectedId],
    );

    const paymentCurrency = payment.currency || agreement.currency || '';
    const amountMismatch = Boolean(
      selected &&
        Math.abs(
          (selected.amountForeign || 0) - (payment.amountAgreement || 0),
        ) > 0.01,
    );

    const onSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selected) return;
        const ok = await linkContribution(selected.id, payment.id);
        if (ok) {
          onLinked?.();
          onClose();
        }
      },
      [selected, linkContribution, payment.id, onLinked, onClose],
    );

    return (
      <Modal>
        <PopupContainer
          width="560px"
          title={t('pages.studentDetail.payments.migration.pickTitle')}
          onClose={onClose}
        >
          <form onSubmit={onSubmit}>
            <L.FormBody>
              <div style={{ fontSize: 13, color: '#475569' }}>
                {t('pages.studentDetail.payments.migration.pickSummary', {
                  date: moment(payment.date).format('DD.MM.YYYY'),
                  amount: formatMoneyCurrency(
                    payment.amountAgreement,
                    paymentCurrency,
                  ),
                })}
              </div>

              {candidates.length === 0 ? (
                <L.Muted>
                  {t('pages.studentDetail.payments.migration.noCandidates')}
                </L.Muted>
              ) : (
                <L.SmallTable>
                  <thead>
                    <tr>
                      <th aria-label="pick" style={{ width: 28 }} />
                      <th>
                        {t('pages.studentDetail.payments.schedule.colDate')}
                      </th>
                      <th style={{ textAlign: 'right' }}>
                        {t('pages.studentDetail.payments.schedule.colPlan')}
                      </th>
                      <th>
                        {t('pages.studentDetail.payments.schedule.colStatus')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => {
                      const isSelected = c.id === selectedId;
                      const overdue = (c.delay ?? 0) > 0;
                      return (
                        <tr
                          key={c.id}
                          style={{
                            cursor: 'pointer',
                            background: isSelected ? '#eff6ff' : undefined,
                          }}
                          onClick={() => setSelectedId(c.id)}
                        >
                          <td>
                            <input
                              type="radio"
                              name="pick-contribution"
                              checked={isSelected}
                              onChange={() => setSelectedId(c.id)}
                              aria-label={c.date}
                            />
                          </td>
                          <td>{moment(c.date).format('DD.MM.YYYY')}</td>
                          <td style={{ textAlign: 'right' }}>
                            <MoneyCell
                              amount={c.amountForeign}
                              currency={agreement.currency || ''}
                            />
                          </td>
                          <td>
                            <L.Tag tone={overdue ? 'danger' : 'neutral'}>
                              {t(
                                `pages.studentDetail.payments.schedule.status.${
                                  overdue ? 'overdue' : 'upcoming'
                                }`,
                              )}
                            </L.Tag>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </L.SmallTable>
              )}

              {amountMismatch ? (
                <L.InlineNotice>
                  {t('pages.studentDetail.payments.linkPopup.amountMismatch', {
                    paymentAmount: formatMoneyCurrency(
                      payment.amountAgreement,
                      paymentCurrency,
                    ),
                    invoiceAmount: formatMoneyCurrency(
                      selected?.amountForeign,
                      agreement.currency || '',
                    ),
                  })}
                </L.InlineNotice>
              ) : null}

              <L.FormFooter>
                <L.PrimaryActionButton
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                >
                  {t('pages.studentDetail.payments.linkPopup.cancel')}
                </L.PrimaryActionButton>
                <L.PrimaryActionButton
                  type="submit"
                  tone="primary"
                  disabled={!selected || loading}
                >
                  {loading
                    ? t('pages.studentDetail.payments.linkPopup.submitting')
                    : t('pages.studentDetail.payments.migration.pickSubmit')}
                </L.PrimaryActionButton>
              </L.FormFooter>
            </L.FormBody>
          </form>
        </PopupContainer>
      </Modal>
    );
  },
);
